import React, { createContext, useEffect, useReducer, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import toast from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_API_PATH || "https://api.accounts.spiderworks.org/api/";

const TOKEN_LIFETIME_MS = 59 * 60 * 1000; // 59 minutes (refresh before 1h JWT expiry)
const REFRESH_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  token: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, token } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      token,
    };
  },
  LOGIN: (state, action) => {
    const { user, token } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
      token,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    token: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setUserData: () => Promise.resolve(),
});

const clearLocalStorage = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("tokenExpiry");
  // Clean up legacy key
  localStorage.removeItem("loginTimestamp");
};

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const token = window.localStorage.getItem("accessToken");
        const userId = window.localStorage.getItem("userId");
        const userName = window.localStorage.getItem("userName");
        const userEmail = window.localStorage.getItem("userEmail");
        const tokenExpiry = window.localStorage.getItem("tokenExpiry");

        if (token && userId && userName && userEmail) {
          const isExpired = !tokenExpiry || Date.now() > parseInt(tokenExpiry, 10);

          if (isExpired) {
            clearLocalStorage();
            dispatch({
              type: "INITIALIZE",
              payload: { isAuthenticated: false, user: null, token: null },
            });
          } else {
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: true,
                user: { id: userId, name: userName, email: userEmail },
                token,
              },
            });
          }
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: { isAuthenticated: false, user: null, token: null },
          });
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        dispatch({
          type: "INITIALIZE",
          payload: { isAuthenticated: false, user: null, token: null },
        });
      }
    };
    initialize();
  }, []);

  // Token refresh logic — check every minute, refresh when near expiry
  const refreshToken = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    const tokenExpiry = localStorage.getItem("tokenExpiry");
    if (!token || !tokenExpiry) return;

    const timeRemaining = parseInt(tokenExpiry, 10) - Date.now();

    // Refresh when less than 2 minutes remain
    if (timeRemaining > 2 * 60 * 1000) return;

    try {
      const response = await axios.post(
        `${BASE_URL}auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data?.status === "success" && response.data.data?.token) {
        const newToken = response.data.data.token;
        localStorage.setItem("accessToken", newToken);
        localStorage.setItem("tokenExpiry", (Date.now() + TOKEN_LIFETIME_MS).toString());

        dispatch({
          type: "LOGIN",
          payload: {
            user: state.user,
            token: newToken,
          },
        });
      }
    } catch (err) {
      console.error("Token refresh failed:", err);
      // If refresh fails, log the user out
      clearLocalStorage();
      dispatch({ type: "LOGOUT" });
    }
  }, [state.user]);

  useEffect(() => {
    let intervalId;
    if (state.isAuthenticated) {
      intervalId = setInterval(refreshToken, REFRESH_CHECK_INTERVAL_MS);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [state.isAuthenticated, refreshToken]);

  const setUserData = async (userData) => {
    try {
      const { token, id, name, email } = userData;

      localStorage.setItem("accessToken", token);
      localStorage.setItem("userId", id);
      localStorage.setItem("userName", name);
      localStorage.setItem("userEmail", email);
      localStorage.setItem("tokenExpiry", (Date.now() + TOKEN_LIFETIME_MS).toString());

      dispatch({
        type: "LOGIN",
        payload: {
          user: { id, name, email },
          token,
        },
      });

      return true;
    } catch (err) {
      console.error("Error setting user data:", err);
      toast.error("Authentication failed");
      return false;
    }
  };

  const logout = async () => {
    // Call server-side logout to revoke the token
    const token = localStorage.getItem("accessToken");
    if (token) {
      try {
        await axios.post(
          `${BASE_URL}auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );
      } catch (err) {
        // Best-effort — continue with local cleanup even if server call fails
        console.error("Server logout failed:", err);
      }
    }

    clearLocalStorage();
    dispatch({ type: "LOGOUT" });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login: () => Promise.resolve(),
        logout,
        setUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
